import React, { useState } from "react";
import { Printer, Save } from "lucide-react";
import { toast } from "sonner";
import { useProgress } from "../../context/ProgressContext";
import { Slider } from "../ui/slider";

const UtilizationMeter = () => {
  const [limit, setLimit] = useState(1000);
  const [balance, setBalance] = useState(250);
  const pct = Math.min(100, Math.round((balance / limit) * 100));
  const status = pct < 10 ? ["Excellent - under 10%", "#2e8b57"] : pct < 30 ? ["Good - under 30%", "#63c79a"] : pct < 50 ? ["Too high - pay it down", "#f7931e"] : ["Danger zone", "#e5356f"];
  return (
    <div className="bg-white rounded-3xl shadow-md p-6 sm:p-8 mb-8" data-testid="utilization-meter">
      <h4 className="font-bold text-[#0c4a6e] text-xl" style={{ fontFamily: "Literata, serif" }}>Utilization meter</h4>
      <p className="text-gray-600 text-sm mt-1">Drag the sliders. Utilization = balance / limit.</p>
      <div className="grid sm:grid-cols-2 gap-6 mt-6">
        <div>
          <div className="flex justify-between text-sm font-bold text-gray-700"><span>Credit limit</span><span>${limit.toLocaleString()}</span></div>
          <Slider value={[limit]} min={200} max={5000} step={50} onValueChange={(v) => { setLimit(v[0]); if (balance > v[0]) setBalance(v[0]); }} className="mt-3" />
        </div>
        <div>
          <div className="flex justify-between text-sm font-bold text-gray-700"><span>Current balance</span><span>${balance.toLocaleString()}</span></div>
          <Slider value={[balance]} min={0} max={limit} step={10} onValueChange={(v) => setBalance(v[0])} className="mt-3" />
        </div>
      </div>
      <div className="mt-6">
        <div className="h-5 rounded-full bg-[#e9f9fd] overflow-hidden relative">
          <div className="h-full rounded-full transition-[width,background-color] duration-300" style={{ width: `${pct}%`, background: status[1] }} />
          <span className="absolute top-0 bottom-0 border-l-2 border-dashed border-[#0c4a6e]" style={{ left: "30%" }} />
          <span className="absolute top-0 bottom-0 border-l-2 border-dashed border-[#0c4a6e]" style={{ left: "10%" }} />
        </div>
        <div className="flex justify-between mt-2 items-center">
          <span className="font-logo text-3xl" style={{ color: status[1] }} data-testid="utilization-pct">{pct}%</span>
          <span className="font-bold text-sm" style={{ color: status[1] }}>{status[0]}</span>
        </div>
      </div>
    </div>
  );
};

const Worksheet = ({ m, onDone }) => {
  const { worksheets, setWorksheetCell } = useProgress();
  const data = worksheets[m.id] || {};
  const ws = m.worksheet;

  const value = (r, c) => {
    const preset = ws.rows[r]?.[c];
    if (preset !== undefined) return preset;
    return data[`${r}-${c}`] || "";
  };

  const filled = Object.values(data).filter((v) => v && v.trim()).length;

  const save = () => {
    if (filled === 0) {
      toast("Fill in at least one cell to save your worksheet.");
      return;
    }
    toast.success("Worksheet saved in this browser");
    onDone();
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="worksheet">
      {m.id === "m4" && <UtilizationMeter />}
      <div className="bg-white rounded-3xl shadow-md p-6 sm:p-8 print-area">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest font-bold" style={{ color: m.dark }}>Worksheet</p>
            <h3 className="text-2xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }}>{ws.title}</h3>
            <p className="text-gray-600 mt-1">{ws.intro}</p>
          </div>
          <button onClick={() => window.print()} className="no-print inline-flex items-center gap-2 btn-pill bg-white border-2 border-[#1b8bc9] text-[#1b8bc9] hover:bg-[#e9f9fd]" data-testid="print-worksheet-button"><Printer size={18} /> Print</button>
        </div>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
            <thead>
              <tr>
                {ws.columns.map((c, i) => (
                  <th key={i} className="text-left font-bold text-white px-3 py-3 first:rounded-l-xl last:rounded-r-xl" style={{ background: m.button }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ws.rows.map((row, r) => (
                <tr key={r} className="odd:bg-[#f7fdfe]">
                  {ws.columns.map((_, c) => {
                    const preset = row[c] !== undefined;
                    return (
                      <td key={c} className="px-2 py-1.5 border-b border-[#e9f9fd] align-top min-w-[120px]">
                        {preset ? (
                          <span className="block px-2 py-2 font-semibold text-[#0c4a6e]">{row[c]}</span>
                        ) : (
                          <input
                            value={value(r, c)}
                            onChange={(e) => setWorksheetCell(m.id, `${r}-${c}`, e.target.value)}
                            className="w-full bg-transparent border-b-2 border-dashed border-[#c9ecf5] focus:border-[#1b8bc9] focus:outline-none px-2 py-2 rounded-md transition-colors"
                            placeholder="..."
                            data-testid={`ws-cell-${r}-${c}`}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-center mt-8 no-print">
        <p className="text-sm text-gray-500 mb-3">{filled} cell{filled === 1 ? "" : "s"} filled - saved automatically in this browser</p>
        <button onClick={save} className="btn-pill-lg text-white shadow-lg inline-flex items-center gap-2" style={{ background: m.button }} data-testid="worksheet-done-button"><Save size={20} /> Save & continue</button>
      </div>
    </div>
  );
};

export default Worksheet;
